/**
 * Rating Widget
 *
 * Number-based rating selector for assessments
 */

import type { WidgetProps } from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';
import { Card, CardContent } from '@tonnie/ui';
import { useCallback, useState } from 'react';

interface IRatingConfig {
  title?: string;
  description?: string;
  min?: number;
  max?: number;
  initialValue?: number;
  /**
   * When provided the widget reports { [variableName]: value } so the engine
   * stores it as a named variable and it appears in the onComplete payload.
   */
  variableName?: string;
  labels?: {
    min?: string;
    max?: string;
  };
}

type IRatingWidgetProps = WidgetProps & {
  config?: IRatingConfig;
};

export function RatingWidget({ config, onComplete }: IRatingWidgetProps) {
  const { t } = useTranslation();
  const min = config?.min || 1;
  const max = config?.max || 5;
  const [selectedValue, setSelectedValue] = useState<number | null>(config?.initialValue || null);

  const handleSelect = useCallback(
    (value: number) => {
      setSelectedValue(value);
      // Report as named variable object so CoPilotGuide stores it in engine variables
      if (config?.variableName) {
        onComplete?.({ [config.variableName]: value });
      } else {
        onComplete?.(value);
      }
    },
    [onComplete, config]
  );

  const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  return (
    <Card className="border-border/50 shadow-sm" data-testid="widget-rating">
      <CardContent className="pt-6">
        {config?.title && <h3 className="text-base font-semibold mb-2">{config.title}</h3>}
        {config?.description && (
          <p className="text-sm text-muted-foreground mb-4">{config.description}</p>
        )}
        <div className="space-y-4">
          {(config?.labels?.min || config?.labels?.max) && (
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{config.labels?.min || t('widgets.rating.notAtAll')}</span>
              <span>{config.labels?.max || t('widgets.rating.veryMuch')}</span>
            </div>
          )}
          <div className="flex gap-2 justify-center">
            {values.map((value) => (
              <button
                key={value}
                onClick={() => handleSelect(value)}
                data-testid={`widget-rating-star-${value}`}
                className={`w-12 h-12 rounded-full font-semibold text-sm transition-all ${
                  selectedValue === value
                    ? 'bg-primary text-primary-foreground shadow-md scale-110'
                    : 'bg-muted hover:bg-muted/80 text-foreground border border-border hover:scale-105'
                }`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default RatingWidget;
